import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  useSearchParams,
  useLoaderData,
  useRevalidator,
} from 'react-router-dom';
import { Snackbar, Snack } from '@nike/eds';
import { useAtom } from 'jotai';
import NikeDictionary from '../../components/nikeDictionary';
import DictionaryItemNew from '../../components/dictionaryItemNew';
import { getDictionary, createDictionaryTerm } from '../../clientAPI';
import { groupTermsByAlphabet } from '../../utils/alphabetHelpers';
import propagate403Error from '../../utils/propagate403Error';
import { QUERY_KEYS, DICTIONARY_TYPE } from '../../constants';
import {
  dictionaryUpdateMessageAtom,
  dictionaryUpdateHasErrorAtom,
} from '../../atoms/dictionary';
import { isExistScope } from '../../utils/authHelpers';

export default function NikeNOGODict() {
  const [updateMsg, setUpdateMsg] = useAtom(dictionaryUpdateMessageAtom);
  const [updateHasError, setUpdateHasError] = useAtom(
    dictionaryUpdateHasErrorAtom,
  );
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const indexKeyFromUrl = searchParams.get('indexKey') as string;
  const loaderData = useLoaderData() as { access_token: string };
  const jwt = loaderData.access_token;
  const canWrite = isExistScope('search360:dictionary:write', jwt);
  const revalidator = useRevalidator();
  const { status, data, error } = useQuery({
    queryKey: [QUERY_KEYS.NOGO_DIC],
    queryFn: async () => getDictionary(DICTIONARY_TYPE.NOGO),
    onError: (err: { status: number }) =>
      err.status === 401 && revalidator.revalidate(),
  });
  const mutation = useMutation({
    mutationFn: (data: DictionaryTermPayload) => {
      return createDictionaryTerm(data, DICTIONARY_TYPE.NOGO);
    },
    onSuccess: () => {
      setUpdateMsg('Created!');
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.NOGO_DIC] });
    },
    onError: (err: { status: number }) => {
      console.log(err);
      setUpdateMsg(JSON.stringify(err));
      setUpdateHasError(true);
      err.status && revalidator.revalidate();
    },
  });
  const termsData =
    status === 'success'
      ? groupTermsByAlphabet(data.terms).filter((groupedTerms) =>
          !indexKeyFromUrl || indexKeyFromUrl === 'ALL'
            ? true
            : groupedTerms.char === String(indexKeyFromUrl).toUpperCase(),
        )
      : [];

  const onCreateFn = (data: DictionaryTermPayload) => {
    mutation.mutate(data);
  };
  propagate403Error(error as keyable);
  return (
    <>
      <NikeDictionary
        activeKey={indexKeyFromUrl}
        status={status}
        data={termsData}
        dictionaryType={DICTIONARY_TYPE.NOGO}
        queryKey={QUERY_KEYS.NOGO_DIC}
      />
      {canWrite && (
        <DictionaryItemNew
          onCreateFn={onCreateFn}
          status={mutation.status}
          error={JSON.stringify(mutation.error)}
        />
      )}

      <Snackbar style={{ left: '90%', top: '90%' }}>
        {updateMsg && (
          <Snack
            autoDismissDuration={5000}
            id='dictionary-nike-custom-update-error-notification'
            onDismiss={(id) => {
              setUpdateMsg('');
            }}
            status={updateHasError ? 'error' : 'success'}
          >
            {updateMsg}
          </Snack>
        )}
      </Snackbar>
    </>
  );
}
